import { COMMERCE_ERROR, CommerceError } from '@loop-coffee/commerce-contracts';

const PRODUCT_FIELDS = '+metadata,*images,*variants.calculated_price,+variants.inventory_quantity';

function errorFor(status, body) {
    const message = body?.message || `Store API respondeu ${status}`;
    if (status === 404) return new CommerceError(COMMERCE_ERROR.NOT_FOUND, message, { status });
    if (status >= 400 && status < 500) return new CommerceError(COMMERCE_ERROR.INVALID, message, { status });
    return new CommerceError(COMMERCE_ERROR.UNAVAILABLE, message, { status });
}

/**
 * Thin Medusa v2 Store API client. Prices, stock and totals are always read back from the API;
 * the storefront never computes them.
 */
export function createClient({ baseUrl, publishableKey, fetchImpl = (...args) => fetch(...args) }) {
    if (!baseUrl || !publishableKey) {
        throw new TypeError('createClient needs baseUrl and publishableKey');
    }

    async function request(path, { method = 'GET', body, query } = {}) {
        const url = new URL(`${baseUrl}/store${path}`);
        Object.entries(query || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== '') url.searchParams.set(key, value);
        });

        let response;
        try {
            response = await fetchImpl(url.toString(), {
                method,
                credentials: 'include',
                headers: {
                    'content-type': 'application/json',
                    'x-publishable-api-key': publishableKey,
                },
                body: body === undefined ? undefined : JSON.stringify(body),
            });
        } catch (cause) {
            throw new CommerceError(COMMERCE_ERROR.UNAVAILABLE, 'Loja indisponível no momento', { cause });
        }

        const data = await response.json().catch(() => null);
        if (!response.ok) throw errorFor(response.status, data);
        return data;
    }

    return {
        async listRegions() {
            return (await request('/regions')).regions;
        },
        async listProducts({ regionId }) {
            return (await request('/products', { query: { region_id: regionId, fields: PRODUCT_FIELDS } })).products;
        },
        async createCart({ regionId }) {
            return (await request('/carts', { method: 'POST', body: { region_id: regionId } })).cart;
        },
        async getCart(cartId) {
            return (await request(`/carts/${cartId}`)).cart;
        },
        async updateCart(cartId, update) {
            return (await request(`/carts/${cartId}`, { method: 'POST', body: update })).cart;
        },
        async addLineItem(cartId, { variantId, quantity }) {
            return (await request(`/carts/${cartId}/line-items`, {
                method: 'POST',
                body: { variant_id: variantId, quantity },
            })).cart;
        },
        async updateLineItem(cartId, lineId, quantity) {
            return (await request(`/carts/${cartId}/line-items/${lineId}`, { method: 'POST', body: { quantity } })).cart;
        },
        async removeLineItem(cartId, lineId) {
            return (await request(`/carts/${cartId}/line-items/${lineId}`, { method: 'DELETE' })).parent;
        },
        async listShippingOptions(cartId) {
            return (await request('/shipping-options', { query: { cart_id: cartId } })).shipping_options;
        },
        async addShippingMethod(cartId, optionId) {
            return (await request(`/carts/${cartId}/shipping-methods`, {
                method: 'POST',
                body: { option_id: optionId },
            })).cart;
        },
        // Providers the backend enabled for the region; the storefront filters them via the registry.
        async listPaymentProviders(regionId) {
            return (await request('/payment-providers', { query: { region_id: regionId } })).payment_providers;
        },
        async initiatePaymentSession(cartId, providerId) {
            const { payment_collection: collection } = await request('/payment-collections', {
                method: 'POST',
                body: { cart_id: cartId },
            });
            return (await request(`/payment-collections/${collection.id}/payment-sessions`, {
                method: 'POST',
                body: { provider_id: providerId },
            })).payment_collection;
        },
        // Medusa answers { type: 'order', order } or { type: 'cart', cart, error } when the
        // backend revalidation (price/stock) refuses the cart.
        async completeCart(cartId) {
            return request(`/carts/${cartId}/complete`, { method: 'POST' });
        },
        // payment_status is computed by the order detail route; the complete response lacks it.
        async getOrder(orderId) {
            return (await request(`/orders/${orderId}`, { query: { fields: '+payment_status' } })).order;
        },
    };
}
